import { cn } from '@bliks-io/ui';
import { connectorPath, type ProcessPiece } from './model';

export type PieceMotion = 'normal' | 'fast' | 'still';

export const LEAVE_MS = 420;

const STROKE_WIDTH = 1.75;
const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

const DRAW: Record<PieceMotion, string | undefined> = {
  normal: `endless-process-draw 320ms ${EASE} both`,
  fast: `endless-process-draw 150ms ${EASE} both`,
  still: undefined
};
// The step waits for its connector to draw, then springs in.
const ENTER: Record<PieceMotion, string | undefined> = {
  normal: `endless-process-enter 420ms ${SPRING} 220ms both`,
  fast: `endless-process-enter 250ms ${SPRING} 100ms both`,
  still: undefined
};
const RETRACT = `endless-process-retract ${LEAVE_MS}ms ${EASE} forwards`;
const LEAVE = `endless-process-leave ${LEAVE_MS}ms ${EASE} forwards`;

function StepShape({ piece, finished }: { piece: ProcessPiece; finished: boolean }) {
  const { kind, x, y } = piece;
  const className = cn(
    'stroke-bliks-green-500 transition-colors duration-300',
    finished ? 'fill-bliks-green-100' : 'fill-card'
  );
  if (kind === 'event') {
    return <circle cx={x} cy={y} r={9} strokeWidth={STROKE_WIDTH} className={className} />;
  }
  if (kind === 'gateway') {
    return (
      <rect
        x={x - 9}
        y={y - 9}
        width={18}
        height={18}
        rx={2}
        transform={`rotate(45 ${x} ${y})`}
        strokeWidth={STROKE_WIDTH}
        className={className}
      />
    );
  }
  return (
    <rect
      x={x - 24}
      y={y - 13}
      width={48}
      height={26}
      rx={6}
      strokeWidth={STROKE_WIDTH}
      className={className}
    />
  );
}

interface ProcessPieceViewProps {
  piece: ProcessPiece;
  sources: ProcessPiece[];
  finished: boolean;
  leavingIds: ReadonlySet<number>;
  motion: PieceMotion;
}

/** One step with the connectors that lead into it; connectors from a leaving step retract. */
export function ProcessPieceView({
  piece,
  sources,
  finished,
  leavingIds,
  motion
}: ProcessPieceViewProps) {
  return (
    <g>
      {sources.map((source) => (
        <path
          key={source.id}
          d={connectorPath(source, piece)}
          pathLength={100}
          strokeDasharray={100}
          fill="none"
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          className="stroke-bliks-green-500"
          style={{ animation: leavingIds.has(source.id) ? RETRACT : DRAW[motion] }}
        />
      ))}
      <g
        className="origin-center transform-fill"
        style={{ animation: leavingIds.has(piece.id) ? LEAVE : ENTER[motion] }}
      >
        <StepShape piece={piece} finished={finished} />
      </g>
    </g>
  );
}
